import React, { Component } from 'react';
import { browserHistory } from 'react-router';
import PropTypes from 'prop-types';
import MediaQuery from 'react-responsive';
import get from 'lodash/get';

import ContextProfile from './ContextProfile';
import ProviderCircle from './ProviderCircle';
import SearchDialogWithForm from '../common/SearchDialogWithForm';
import MobileNavbar from '../common/MobileNavbar';
import PopoverMenu from '../common/PopoverMenu';
import BackButton from '../common/BackButton';
import Loader from '../common/Loader';
import { CHAT, DIALOG_TITLE, NEW_CHAT } from '../../constants/strings';
import { POPOVER_MENU_ITEMS } from '../../constants';
import { MESSAGES } from '../../constants/routes';
import { BREAKPOINT } from '../../constants/styles';
import { SEARCH_TABS } from '../../constants/props';
import FamilyCircleWrapper from './FamilyCircleWrapper';
import { enhance } from './enhance';
import { GATrackPageView } from '../../gtag';
import MetaHelmet from '../AppHelmet/MetaHelmet';

const MobileNavigation = ({ title, isHandleBack }) => (
  <MediaQuery maxWidth={BREAKPOINT}>
    <MobileNavbar openContext="arrow-down" toggleContext={isHandleBack}>
      <p children={title} />
    </MobileNavbar>
  </MediaQuery>
);

MobileNavigation.propTypes = {
  title: PropTypes.string,
  isHandleBack: PropTypes.func,
};

class CircleOfCare extends Component {
  state = {
    isAssignProviderOpen: false,
    isPopoverOpen: false,
    expanded: false,
    anchorEl: null,
  };

  componentDidMount() {
    GATrackPageView('Circle of Care');

    const {
      getAccessLevels,
      getRelationshipPriority,
      setTargetUserId,
      getCircleOfCare,
      targetUserId,
      params: { id },
    } = this.props;
    const userId = Number(id);

    getCircleOfCare({ id: userId });
    getAccessLevels({ id: userId });
    getRelationshipPriority();

    if (userId !== targetUserId) {
      setTargetUserId({ id: userId });
    }
  }

  componentWillUnmount() {
    const { clearRelationshipPriority } = this.props;
    clearRelationshipPriority();
  }

  get circleMenuItems() {
    const { getMenuItems } = this.props;

    const providerCircleMenuItems = {
      [POPOVER_MENU_ITEMS.UNASSIGN]: this.handleRemoveProvider,
    };

    return getMenuItems(providerCircleMenuItems);
  }

  handleBack = () => {
    browserHistory.push(`/${MESSAGES}`);
    setTimeout(() => {
      this.toggleContext();
    }, 1000);
  };

  handleClearState = () => {
    this.setState({
      isAssignProviderOpen: false,
      isPopoverOpen: false,
    });
  };

  toggleContext = () => {
    if (this.state.expanded) return;
    this.props.toggleContextVisibility();
  };

  handleOpenAssignProvider = () => {
    this.setState(prevState => ({
      isAssignProviderOpen: !prevState.isAssignProviderOpen,
    }));
  };

  handleAssignProvider = (
    selectedIds,
    selectedOption,
    reasonCode,
    relation,
  ) => {
    const { targetUserId, assignProvider } = this.props;

    assignProvider({
      ids: selectedIds,
      userId: targetUserId,
      assignment_status: selectedOption,
      reason_code: reasonCode,
      relationship_priority_id: relation,
    });
  };

  handleRemoveProvider = () => {
    const { removeAssignedProvider, targetUserId } = this.props;
    const { user } = this.state;

    removeAssignedProvider({
      providerId: get(user, 'primaryProviderProfile.id'),
      userId: targetUserId,
    });

    this.handleClearState();
  };

  handleOpenPopover = (e, user) => {
    e.preventDefault();
    e.stopPropagation();

    const { isPopoverOpen } = this.state;

    this.setState({
      anchorEl: e.currentTarget,
      isPopoverOpen: !isPopoverOpen,
      user,
    });
  };

  render() {
    const {
      isLoggedUser,
      targetUserId,
      genderList,
      profileData,
      careProviders,
      loadingProfile,
      loadingCircle,
      canAssignProvider,
      intl: { formatMessage },
      routes,
    } = this.props;

    const { isPopoverOpen, anchorEl, isAssignProviderOpen } = this.state;

    const restrictionPath = isLoggedUser ? 'patient' : 'patient-view';
    const profileLink = `/profile/${profileData.id}/${restrictionPath}`;
    const isProfile = get(routes, '[1].path') === 'profile';
    const rootClassName = !isProfile ? 'g-content' : '';

    return (
      <div className={rootClassName} styleName="root">
        <MetaHelmet title="pages.title.circleOfCare" />

        <If condition={!isProfile}>
          <MediaQuery maxWidth={BREAKPOINT}>
            <MobileNavigation
              title={formatMessage({ id: 'circles.circleOfCare' })}
              isHandleBack={this.handleBack}
            />
          </MediaQuery>
          <div styleName="header">
            <MediaQuery minWidth={BREAKPOINT}>
              <BackButton onBack={this.handleBack} />
            </MediaQuery>
          </div>
        </If>
        {targetUserId && (
          <SearchDialogWithForm
            type="context_provider"
            isOpen={isAssignProviderOpen}
            contextUserId={targetUserId}
            title={formatMessage(DIALOG_TITLE.assignProviderOrTeamToPatient)}
            buttonTitle={formatMessage(DIALOG_TITLE.assign)}
            defaultState={formatMessage(DIALOG_TITLE.defaultProviderAssign)}
            emptyState={formatMessage(NEW_CHAT.noOneFound)}
            searchPlaceholder={formatMessage(DIALOG_TITLE.searchProvider)}
            onClose={this.handleOpenAssignProvider}
            onCreate={this.handleAssignProvider}
            withTabs={[SEARCH_TABS.PEOPLE, SEARCH_TABS.TEAMS]}
          />
        )}
        <If condition={loadingProfile || loadingCircle}>
          <Loader />
          <Else />
          <div styleName="content">
            <If condition={isPopoverOpen}>
              <PopoverMenu
                size="small"
                isOpen
                anchorEl={anchorEl}
                items={this.circleMenuItems}
                onClose={this.handleClearState}
              />
            </If>
            <If condition={!isProfile}>
              <ContextProfile
                {...{ formatMessage }}
                {...{ profileLink }}
                profileData={profileData}
                genderList={genderList}
              />
            </If>
            <FamilyCircleWrapper />
            <ProviderCircle
              {...{ canAssignProvider }}
              onOpen={this.handleOpenPopover}
              onAssignProvider={this.handleOpenAssignProvider}
              mainCareProviderIndex={-1}
              {...{ careProviders }}
              title={formatMessage(CHAT.titles.providersChatTab)}
              type="provider"
              relatedPatientId={targetUserId}
            />
          </div>
        </If>
      </div>
    );
  }
}

CircleOfCare.propTypes = {
  isLoggedUser: PropTypes.bool,
  loadingProfile: PropTypes.bool,
  loadingCircle: PropTypes.bool,
  targetUserId: PropTypes.number,
  genderList: PropTypes.array,
  profileData: PropTypes.object,
  getCircleOfCare: PropTypes.func,
  getUser: PropTypes.func,
  careProviders: PropTypes.array,
  removeAssignedProvider: PropTypes.func,
  canAssignProvider: PropTypes.bool,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }).isRequired,
  routes: PropTypes.array,
  params: PropTypes.object,
  getAccessLevels: PropTypes.func,
  getRelationshipPriority: PropTypes.func,
  clearRelationshipPriority: PropTypes.func,
  setTargetUserId: PropTypes.func,
  assignProvider: PropTypes.func,
  getMenuItems: PropTypes.func,
  toggleContextVisibility: PropTypes.func.isRequired,
};

export default enhance(CircleOfCare);



// WEBPACK FOOTER //
// ./src/components/CircleOfCare/index.jsx